<template>
  <div class="ranger-test-chat">
    <div class="rtc-header">
      <span class="pw-label">Sesión</span>
      <span class="rtc-session pw-value">{{ sessionId }}</span>
      <v-spacer></v-spacer>
      <v-btn text small color="primary" :disabled="sending" @click="resetSession">
        Reiniciar
      </v-btn>
    </div>

    <v-text-field
      v-model="phone"
      label="Teléfono del tutor (opcional)"
      placeholder="+56912345678"
      dense
      outlined
      hide-details
      :disabled="sending || conversationStarted"
    ></v-text-field>

    <div ref="logEl" class="rtc-log">
      <div
        v-for="(m, i) in log"
        :key="i"
        :class="['rtc-bubble', `rtc-bubble--${m.role}`]"
      >
        {{ m.text }}
      </div>
    </div>

    <div class="rtc-meta">
      <div class="rtc-meta-item">
        <span class="pw-label">Fase</span>
        <span class="pw-value">{{ state.phase || '—' }}</span>
      </div>
      <div class="rtc-meta-item">
        <span class="pw-label">Categoría</span>
        <span class="pw-value">{{ state.reasonCategory || '—' }}</span>
      </div>
      <div class="rtc-meta-item">
        <span class="pw-label">Outcome</span>
        <span class="pw-value">{{ state.outcome || '—' }}</span>
      </div>
      <div class="rtc-meta-item">
        <span class="pw-label">Versión de reglas</span>
        <span class="pw-value">{{ state.rulesVersion || '—' }}</span>
      </div>
      <div class="rtc-meta-item rtc-meta-item--full">
        <span class="pw-label">Variables recolectadas</span>
        <span class="pw-value rtc-vars">{{ JSON.stringify(state.collectedVariables) }}</span>
      </div>
    </div>

    <v-form v-if="can('ranger-test-chat:use')" class="rtc-row" @submit.prevent="handleSubmit">
      <v-text-field
        v-model="draft"
        class="rtc-input"
        placeholder="Escribe como lo haría un tutor…"
        dense
        outlined
        hide-details
        :disabled="sending"
      ></v-text-field>
      <v-btn color="primary" type="submit" :disabled="sending || !draft.trim()">
        Enviar
      </v-btn>
    </v-form>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="5000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { utilsMixin } from '@/mixins/utilsMixin';
import RangerService from '@/services/ranger.service';
import { can } from '@/utils/permissions';

export default {
  name: 'RangerTestChat',
  mixins: [utilsMixin],
  props: {
    apiEndpoint: {
      type: String,
      default: '/internal/ranger/test-chat'
    }
  },
  data() {
    return {
      sessionId: this.generateSessionId(),
      phone: '',
      draft: '',
      sending: false,
      log: [],
      state: this.initialState(),
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red'
    };
  },
  computed: {
    conversationStarted() {
      return this.log.some((m) => m.role === 'user');
    }
  },
  mounted() {
    this.pushBubble('sys', `Sesión: ${this.sessionId}`);
  },
  methods: {
    can,
    initialState() {
      return {
        phase: null,
        reasonCategory: null,
        outcome: null,
        rulesVersion: null,
        collectedVariables: {}
      };
    },

    generateSessionId() {
      return `admin_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    },

    pushBubble(role, text) {
      this.log.push({ role, text });
      this.$nextTick(() => {
        if (this.$refs.logEl) this.$refs.logEl.scrollTop = this.$refs.logEl.scrollHeight;
      });
    },

    resetSession() {
      this.sessionId = this.generateSessionId();
      this.log = [];
      this.state = this.initialState();
      this.pushBubble('sys', `Sesión: ${this.sessionId}`);
    },

    async sendMessage(message) {
      // Ranger solo usa `phone` para arrancar la sesión de triage en el primer
      // mensaje; mandarlo en mensajes siguientes no tendría efecto, así que ni
      // se manda (ver contracts/ranger-test-chat.md).
      const phone = this.conversationStarted ? null : this.phone.trim() || null;
      this.pushBubble('user', message);
      this.sending = true;
      try {
        const res = await RangerService.sendTestChatMessage(this.sessionId, message, this.apiEndpoint, phone);
        if (res.status === 'ok') {
          this.pushBubble('bot', res.data.reply || '(sin respuesta)');
          this.state = {
            phase: res.data.phase,
            reasonCategory: res.data.reasonCategory,
            outcome: res.data.outcome,
            rulesVersion: res.data.rulesVersion,
            collectedVariables: res.data.collectedVariables
          };
        } else {
          this.pushBubble('sys', 'No se pudo obtener una respuesta de Ranger. Intenta nuevamente.');
          this.showErrorSnackbar('Error al conversar con Ranger');
        }
      } finally {
        this.sending = false;
      }
    },

    async handleSubmit() {
      const text = this.draft.trim();
      if (!text || this.sending) return;
      this.draft = '';
      await this.sendMessage(text);
    }
  }
};
</script>

<style scoped>
.ranger-test-chat {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 640px;
}

.rtc-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rtc-session {
  font-family: monospace;
}

.rtc-log {
  height: 420px;
  overflow-y: auto;
  border: 1px solid var(--pw-border);
  border-radius: var(--pw-radius-card, 12px);
  background: var(--pw-surface);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rtc-bubble {
  max-width: 80%;
  padding: 8px 12px;
  border-radius: var(--pw-radius-control, 8px);
  font-size: 13px;
  white-space: pre-wrap;
}

.rtc-bubble--bot {
  align-self: flex-start;
  background: var(--pw-surface-subtle);
  color: var(--pw-text);
}

.rtc-bubble--user {
  align-self: flex-end;
  background: var(--pw-primary);
  color: var(--pw-surface);
}

.rtc-bubble--sys {
  align-self: center;
  font-size: 11px;
  color: var(--pw-text-faint);
}

.rtc-meta {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 16px;
  border-top: 1px dashed var(--pw-border);
  padding-top: 10px;
}

.rtc-meta-item--full {
  grid-column: 1 / -1;
}

.rtc-vars {
  word-break: break-all;
  display: block;
}

.rtc-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.rtc-input {
  flex: 1;
}
</style>
